// Sizes & colours for clothing (docs/plans/sizes-colours.md, canvas row `sizes-colours`):
// the editor in Add product (size runs, colour chips and the colour sheet, then a grid of
// colours down and sizes across for opening stock, prices and codes), the product page's
// stock grid, and the sheet a bill picks a size and colour from. Each size and colour is
// still its own product under the group (lib/variants.ts); this is how it is shown.
import type { ProductRow } from '@ims/data'
import { Button, Checkbox, Field, Input, Modal, Segmented, Switch, cx } from '@ims/ui'
import { Check, Minus, Plus, X } from '@ims/ui/icons'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { MoneyInput, QtyInput } from '../components.tsx'
import { useLive } from '../hooks.ts'
import {
  PATTERNS,
  READY_COLOURS,
  SHADES,
  normalHex,
  readSwatches,
  readySwatch,
  shopColours,
  swatchBackground,
  type ReadyColour,
} from '../lib/colours.ts'
import { money, qty } from '../lib/format.ts'
import { useOpen } from '../lib/motion.ts'
import { SIZE_RUNS, runLabel, shopRuns, sortSizes } from '../lib/sizes.ts'
import {
  COLOUR_KEY,
  SIZE_KEY,
  cellOf,
  cellValues,
  comboKey,
  parseOptionValues,
  readOptions,
  readValues,
  sizeColourAxes,
  variantLabel,
  type SizeColourAxes,
} from '../lib/variants.ts'
import { useNarrow } from '../lib/wide.ts'
import { PhotoSlot, ProductPhoto, usePhotoUrls } from '../photos.tsx'
import { ScanButton } from '../scanner.tsx'
import { draftOptions, type OptionDraft, type VariantDraft, type VariantEdit } from './variants.tsx'

type Settings = Parameters<typeof money>[1]

/** A colour's dot (or pattern); nothing for a colour without one. */
export function Swatch({ swatch, className }: { swatch: string | undefined; className?: string }) {
  if (!swatch) return null
  return (
    <span
      aria-hidden
      className={cx(
        'inline-block size-3.5 shrink-0 rounded-full ring-1 ring-ink/15 ring-inset',
        className,
      )}
      style={{ background: swatchBackground(swatch) }}
    />
  )
}

/** A group's colour dots in a row (the products list): up to six, then a count. */
export function ColourDots({ swatches }: { swatches: Record<string, string> }) {
  const names = Object.keys(swatches)
  if (names.length === 0) return null
  return (
    <span className="mr-1.5 inline-flex items-center gap-0.5 align-[-2px]" title={names.join(', ')}>
      {names.slice(0, 6).map((n) => (
        <Swatch key={n} swatch={swatches[n]} className="size-2.5" />
      ))}
      {names.length > 6 && <span className="ml-0.5 text-xs text-muted">+{names.length - 6}</span>}
    </span>
  )
}

/** The axes a form's options use: the group's own keys, or Size and Colour for a new one. */
export function formAxes(options: readonly OptionDraft[]): SizeColourAxes {
  const axes = sizeColourAxes(draftOptions(options))
  const named = sizeColourAxes(
    Object.fromEntries(options.filter((o) => o.name.trim()).map((o) => [o.name.trim(), ['x']])),
  )
  return {
    size: axes?.size ?? named?.size ?? SIZE_KEY,
    colour: axes?.colour ?? named?.colour ?? COLOUR_KEY,
  }
}

/** Whether a form's options can be edited as sizes and colours (no other option). */
export function isSizeColourForm(options: readonly OptionDraft[]): boolean {
  return options.every((o) => {
    const k = o.name.trim().toLowerCase()
    return !o.name.trim() || k === 'size' || k === 'colour' || k === 'color'
  })
}

/** The size and colour lists in a form's options. */
export function formLists(options: readonly OptionDraft[]) {
  const axes = formAxes(options)
  const find = (name: string | null) =>
    parseOptionValues(options.find((o) => o.name.trim() === name)?.values ?? '')
  return { axes, sizes: find(axes.size), colours: find(axes.colour) }
}

function writeLists(
  options: readonly OptionDraft[],
  axes: SizeColourAxes,
  sizes: readonly string[],
  colours: readonly string[],
): OptionDraft[] {
  const keyOf = (name: string | null) =>
    options.find((o) => o.name.trim() === name)?.key ?? crypto.randomUUID()
  // Commas separate values in the stored text: a name never carries one.
  const clean = (list: readonly string[]) => list.map((v) => v.replaceAll(',', ' ').trim())
  return [
    { key: keyOf(axes.size), name: axes.size ?? SIZE_KEY, values: clean(sizes).join(', ') },
    { key: keyOf(axes.colour), name: axes.colour ?? COLOUR_KEY, values: clean(colours).join(', ') },
  ]
}

/** The shop's groups (their size lists and colour dots), for the quick picks. */
function useShopGroups() {
  return (
    useLive(
      async (repo) =>
        (await repo.list('products', { where: { kind: 'group' }, limit: 5000 })).rows.map((g) => ({
          variant_options: g.variant_options,
          variant_swatches: g.variant_swatches,
        })),
      [],
      ['products'],
    ).data ?? []
  )
}

export function SizesColoursEditor({
  options,
  onOptions,
  drafts,
  onEdit,
  swatches,
  onSwatches,
  photos,
  onPhoto,
  photosOn,
  groupPrice,
  opening,
  allowsDecimal,
  unitSymbol,
  settings,
}: {
  options: OptionDraft[]
  onOptions: (options: OptionDraft[]) => void
  drafts: VariantDraft[]
  onEdit: (key: string, edit: VariantEdit) => void
  swatches: Record<string, string>
  onSwatches: (swatches: Record<string, string>) => void
  /** Each colour's photo by its name (null: none). */
  photos: Record<string, string | null>
  onPhoto: (colour: string, path: string | null) => void
  photosOn: boolean
  groupPrice: number | null
  /** Opening stock can be typed (a new product that counts stock). */
  opening: boolean
  allowsDecimal: boolean
  unitSymbol: string
  settings: Settings
}) {
  const narrow = useNarrow()
  const groups = useShopGroups()
  const { axes, sizes, colours } = formLists(options)
  const byKey = new Map(drafts.map((d) => [d.key, d]))
  // Sizes and colours already saved stay: their products may have stock and sales.
  const locked = { sizes: new Set<string>(), colours: new Set<string>() }
  for (const d of drafts) {
    if (!d.existing) continue
    const c = cellOf(axes, d.values)
    if (c.size) locked.sizes.add(c.size.toLowerCase())
    if (c.colour) locked.colours.add(c.colour.toLowerCase())
  }
  const setLists = (nextSizes: readonly string[], nextColours: readonly string[]) =>
    onOptions(writeLists(options, axes, nextSizes, nextColours))

  const sizeCols: (string | null)[] = sizes.length > 0 ? sizes : [null]
  const colourRows: (string | null)[] = colours.length > 0 ? colours : [null]
  const cell = (size: string | null, colour: string | null) =>
    byKey.get(comboKey(cellValues(axes, size, colour)))
  const included = colourRows.flatMap((c) =>
    sizeCols.map((s) => cell(s, c)).filter((d): d is VariantDraft => !!d && d.include),
  )
  const labelOf = (d: VariantDraft) => variantLabel(d.values, draftOptions(options)) || 'One size'

  // Prices: one for all, unless some sizes have their own.
  const sizePrice = (size: string | null) =>
    colourRows.map((c) => cell(size, c)).find((d) => d)?.pricePaisa ?? null
  const ownPrices = drafts.some((d) => d.pricePaisa !== null && d.pricePaisa !== groupPrice)
  const [bySize, setBySize] = useState(ownPrices)
  const setSizePrice = (size: string | null, paisa: number | null) => {
    for (const c of colourRows) {
      const d = cell(size, c)
      if (d) onEdit(d.key, { pricePaisa: paisa })
    }
  }

  type Tab = 'opening' | 'prices' | 'codes'
  const tabs: { value: Tab; label: string }[] = [
    ...(opening ? [{ value: 'opening' as const, label: 'Opening stock' }] : []),
    ...(bySize ? [{ value: 'prices' as const, label: 'Prices' }] : []),
    { value: 'codes', label: 'Codes' },
  ]
  const [tabChoice, setTab] = useState<Tab>(opening ? 'opening' : 'codes')
  const tab = tabs.some((t) => t.value === tabChoice) ? tabChoice : tabs[0]!.value
  const [phoneColour, setPhoneColour] = useState<string | null>(null)
  const shownColour =
    phoneColour !== null && colours.includes(phoneColour) ? phoneColour : (colourRows[0] ?? null)

  const [sheet, setSheet] = useState(false)
  const runs = [
    ...SIZE_RUNS,
    ...shopRuns(groups.map((g) => readOptions(g.variant_options).Size ?? [])),
  ]
  const keptSizes = sizes.filter((s) => locked.sizes.has(s.toLowerCase()))
  const sameList = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((v, i) => v.toLowerCase() === b[i]!.toLowerCase())

  const addSizes = (text: string) => {
    const typed = parseOptionValues(text)
    if (typed.length > 0) setLists(sortSizes([...sizes, ...typed]), colours)
  }
  const removeColour = (name: string) => {
    setLists(
      sizes,
      colours.filter((c) => c !== name),
    )
    const next = { ...swatches }
    delete next[name]
    onSwatches(next)
  }
  const toggleColour = (c: ReadyColour) => {
    const has = colours.find((x) => x.toLowerCase() === c.name.toLowerCase())
    if (has) {
      if (!locked.colours.has(has.toLowerCase())) removeColour(has)
      return
    }
    setLists(sizes, [...colours, c.name])
    if (c.swatch) onSwatches({ ...swatches, [c.name]: c.swatch })
  }

  const total = (list: (VariantDraft | undefined)[]) =>
    list.reduce((a, d) => a + (d && d.include && !d.existing ? (d.openingMilli ?? 0) : 0), 0)

  return (
    <div className="min-w-0 space-y-5 sm:col-span-2">
      <section aria-label="Sizes">
        <h3 className="text-sm font-semibold">Sizes</h3>
        <p className="mb-2 text-xs text-muted">Tap a size run, or type your own and press Enter.</p>
        <div className="mb-2 flex flex-wrap gap-2">
          {runs.map((r) => (
            <button
              key={`${r.name}-${r.sizes.join('|')}`}
              type="button"
              aria-pressed={sameList(sizes, r.sizes)}
              onClick={() => setLists(sortSizes([...keptSizes, ...r.sizes]), colours)}
              className={cx(
                'rounded-xl border px-3 py-1.5 text-left',
                sameList(sizes, r.sizes)
                  ? 'border-primary bg-primary-light'
                  : 'border-border bg-surface hover:border-primary/40',
              )}
            >
              <span className="block text-sm font-semibold">{r.name}</span>
              <span className="block text-xs text-muted">{runLabel(r.sizes)}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sizes.map((s) => (
            <Chip
              key={s}
              onRemove={
                locked.sizes.has(s.toLowerCase())
                  ? undefined
                  : () =>
                      setLists(
                        sizes.filter((x) => x !== s),
                        colours,
                      )
              }
              removeLabel={`Remove size ${s}`}
            >
              {s}
            </Chip>
          ))}
          <AddBox label="Add a size" placeholder="Add a size…" onAdd={addSizes} />
        </div>
      </section>

      <section aria-label="Colours">
        <h3 className="text-sm font-semibold">Colours</h3>
        <p className="mb-2 text-xs text-muted">
          Tap a ready colour or add your own. The name is what shows on bills; the dot only tells
          them apart.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {colours.map((c) => (
            <Chip
              key={c}
              swatch={swatches[c]}
              onRemove={locked.colours.has(c.toLowerCase()) ? undefined : () => removeColour(c)}
              removeLabel={`Remove colour ${c}`}
            >
              {c}
            </Chip>
          ))}
          <button
            type="button"
            onClick={() => setSheet(true)}
            className="inline-flex h-8 items-center gap-1 rounded-full border-[1.5px] border-dashed border-border px-3 text-sm font-semibold text-muted hover:border-primary/40 hover:text-ink"
          >
            <Plus className="size-3.5" aria-hidden />
            Colour
          </button>
        </div>
      </section>

      <Switch
        label="Different price for some sizes"
        description={
          bySize
            ? 'Set them in the Prices tab. Every colour of a size has its price.'
            : `Every size and colour sells at ${groupPrice !== null ? money(groupPrice, settings) : 'the price above'}.`
        }
        checked={bySize}
        onChange={(e) => {
          setBySize(e.target.checked)
          if (!e.target.checked)
            for (const d of drafts) if (d.pricePaisa !== null) onEdit(d.key, { pricePaisa: null })
          if (e.target.checked) setTab('prices')
        }}
      />

      {included.length + drafts.filter((d) => !d.include).length > 0 && (
        <div className="space-y-3">
          <Segmented label="Sizes and colours" value={tab} options={tabs} onChange={setTab} />

          {tab === 'opening' && !narrow && (
            <>
              <p className="text-xs text-muted">
                How many of each you have now. A dashed cell is a size you don’t keep in that
                colour: tap it to add it.
              </p>
              <Grid
                sizes={sizeCols}
                colours={colourRows}
                head={(c) => (
                  <ColourHead
                    colour={c}
                    swatch={c ? swatches[c] : undefined}
                    photo={photosOn && c !== null ? (photos[c] ?? null) : undefined}
                    onPhoto={(p) => c !== null && onPhoto(c, p)}
                  />
                )}
                cell={(s, c) => {
                  const d = cell(s, c)
                  if (!d) return null
                  const label = labelOf(d)
                  if (d.existing)
                    return <span className="block text-center text-xs text-subtle">Saved</span>
                  if (!d.include)
                    return (
                      <button
                        type="button"
                        aria-label={`Keep ${label}`}
                        onClick={() => onEdit(d.key, { include: true })}
                        className="h-10 w-full rounded-xl border-[1.5px] border-dashed border-border text-xs text-subtle hover:border-primary/40"
                      >
                        Not stocked
                      </button>
                    )
                  return (
                    <span className="group relative block">
                      <QtyInput
                        aria-label={`Opening stock of ${label}`}
                        milli={d.openingMilli}
                        allowsDecimal={allowsDecimal}
                        placeholder="0"
                        className="text-center"
                        onValue={(m) => onEdit(d.key, { openingMilli: m })}
                      />
                      <button
                        type="button"
                        aria-label={`Don’t keep ${label}`}
                        title="Not stocked"
                        onClick={() => onEdit(d.key, { include: false, openingMilli: null })}
                        className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full border border-border bg-surface text-muted opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100 hover:text-ink"
                      >
                        <X className="size-3" aria-hidden />
                      </button>
                    </span>
                  )
                }}
                rowTotal={(c) => qty(total(sizeCols.map((s) => cell(s, c))))}
                colTotal={(s) => qty(total(colourRows.map((c) => cell(s, c))))}
                allTotal={qty(total(included))}
              />
            </>
          )}

          {tab === 'opening' && narrow && (
            <div>
              <p className="mb-2 text-xs text-muted">One colour at a time.</p>
              <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1">
                {colourRows.map((c) => (
                  <button
                    key={c ?? 'none'}
                    type="button"
                    aria-pressed={c === shownColour}
                    onClick={() => setPhoneColour(c)}
                    className={cx(
                      'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold',
                      c === shownColour
                        ? 'border-primary bg-sell-wash'
                        : 'border-border bg-surface',
                    )}
                  >
                    <Swatch swatch={c ? swatches[c] : undefined} />
                    {c ?? 'All'}
                    <span className="font-medium text-muted tabular-nums">
                      {qty(total(sizeCols.map((s) => cell(s, c))))}
                    </span>
                  </button>
                ))}
              </div>
              {photosOn && shownColour !== null && (
                <div className="mb-2 flex items-center gap-3 rounded-xl border border-border p-2.5">
                  <PhotoSlot
                    path={photos[shownColour] ?? null}
                    onChange={(p) => onPhoto(shownColour, p)}
                    name={shownColour}
                  />
                  <div className="min-w-0 text-sm">
                    <div className="font-semibold">Photo of {shownColour}</div>
                    <div className="text-xs text-muted">Every {shownColour} size shows it.</div>
                  </div>
                </div>
              )}
              <ul>
                {sizeCols.map((s) => {
                  const d = cell(s, shownColour)
                  if (!d) return null
                  const label = labelOf(d)
                  return (
                    <li
                      key={s ?? 'one'}
                      className="flex items-center gap-2 border-t border-border py-2"
                    >
                      <span className="w-14 font-semibold">{s ?? 'One size'}</span>
                      {d.existing ? (
                        <span className="flex-1 text-right text-xs text-subtle">Saved</span>
                      ) : !d.include ? (
                        <>
                          <span className="flex-1 text-sm text-subtle">
                            Not stocked in this colour
                          </span>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onEdit(d.key, { include: true })}
                          >
                            Add
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1" />
                          <Stepper
                            label={label}
                            milli={d.openingMilli}
                            allowsDecimal={allowsDecimal}
                            onValue={(m) => onEdit(d.key, { openingMilli: m })}
                          />
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {tab === 'prices' && (
            <div className="space-y-2">
              <p className="text-xs text-muted">
                A size left empty sells at{' '}
                {groupPrice !== null ? money(groupPrice, settings) : 'the price above'}.
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {sizeCols.map((s) => (
                  <Field key={s ?? 'one'} label={s ?? 'One size'}>
                    {(id) => (
                      <MoneyInput
                        id={id}
                        aria-label={`Price of size ${s ?? 'one size'}`}
                        paisa={sizePrice(s)}
                        placeholder={groupPrice !== null ? money(groupPrice, settings) : '0.00'}
                        onValue={(p) => setSizePrice(s, p)}
                      />
                    )}
                  </Field>
                ))}
              </div>
            </div>
          )}

          {tab === 'codes' && <Codes drafts={included} labelOf={labelOf} onEdit={onEdit} />}

          <p className="text-xs text-muted">
            <b className="text-ink">
              {included.filter((d) => !d.existing).length}{' '}
              {included.filter((d) => !d.existing).length === 1 ? 'product' : 'products'}
            </b>{' '}
            will be made, one for each size and colour, each with its own stock and barcode
            {opening && total(included) > 0
              ? ` (${[qty(total(included)), unitSymbol].filter(Boolean).join(' ')} in stock)`
              : ''}
            . Shared: name, category, VAT, brand.
          </p>
        </div>
      )}

      {sheet && (
        <ColourSheet
          chosen={colours}
          locked={locked.colours}
          shop={shopColours(groups)}
          onToggle={toggleColour}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  )
}

function Chip({
  children,
  swatch,
  onRemove,
  removeLabel,
}: {
  children: ReactNode
  swatch?: string
  onRemove?: () => void
  removeLabel: string
}) {
  return (
    <span
      className={cx(
        'inline-flex h-8 items-center gap-1.5 rounded-full border-[1.5px] border-primary bg-sell-wash text-sm font-semibold',
        swatch ? 'pl-2.5' : 'pl-3',
        onRemove ? 'pr-1' : 'pr-3',
      )}
    >
      <Swatch swatch={swatch} />
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="tap-target flex size-6 items-center justify-center rounded-full text-muted hover:bg-soft hover:text-ink"
        >
          <X className="size-3" aria-hidden />
        </button>
      )}
    </span>
  )
}

function AddBox({
  label,
  placeholder,
  onAdd,
}: {
  label: string
  placeholder: string
  onAdd: (text: string) => void
}) {
  const [text, setText] = useState('')
  const add = () => {
    if (!text.trim()) return
    onAdd(text)
    setText('')
  }
  return (
    <span className="w-36">
      <Input
        aria-label={label}
        value={text}
        placeholder={placeholder}
        className="h-8 rounded-full text-sm"
        onChange={(e) => setText(e.target.value)}
        onBlur={add}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
      />
    </span>
  )
}

function ColourHead({
  colour,
  swatch,
  photo,
  onPhoto,
}: {
  colour: string | null
  swatch: string | undefined
  /** undefined: photos are off. */
  photo?: string | null
  onPhoto: (path: string | null) => void
}) {
  return (
    <span className="inline-flex items-center gap-2.5 font-semibold">
      {photo !== undefined && colour !== null ? (
        <PhotoSlot path={photo} onChange={onPhoto} name={colour} />
      ) : (
        <Swatch swatch={swatch} />
      )}
      {colour ?? 'Stock'}
    </span>
  )
}

/** Colours down, sizes across, with totals: the editor's and the product page's grid. */
function Grid({
  sizes,
  colours,
  head,
  cell,
  rowTotal,
  colTotal,
  allTotal,
  extraRow,
}: {
  sizes: (string | null)[]
  colours: (string | null)[]
  head: (colour: string | null) => ReactNode
  cell: (size: string | null, colour: string | null) => ReactNode
  rowTotal?: (colour: string | null) => ReactNode
  colTotal?: (size: string | null) => ReactNode
  allTotal?: ReactNode
  extraRow?: ReactNode
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-40" />
            {sizes.map((s) => (
              <th
                key={s ?? 'one'}
                scope="col"
                className="min-w-16 px-1 py-1.5 text-center font-bold"
              >
                {s ?? 'One size'}
              </th>
            ))}
            {rowTotal && (
              <th scope="col" className="px-2 py-1.5 text-right text-xs font-semibold text-muted">
                Total
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {extraRow}
          {colours.map((c) => (
            <tr key={c ?? 'none'}>
              <th scope="row" className="py-1 pr-3 text-left whitespace-nowrap">
                {head(c)}
              </th>
              {sizes.map((s) => (
                <td key={s ?? 'one'} className="p-1">
                  {cell(s, c)}
                </td>
              ))}
              {rowTotal && (
                <td className="px-2 text-right font-bold tabular-nums">{rowTotal(c)}</td>
              )}
            </tr>
          ))}
        </tbody>
        {colTotal && (
          <tfoot>
            <tr>
              <th scope="row" className="pt-1.5 pr-3 text-left text-xs font-semibold text-muted">
                All colours
              </th>
              {sizes.map((s) => (
                <td
                  key={s ?? 'one'}
                  className="pt-1.5 text-center font-bold text-muted tabular-nums"
                >
                  {colTotal(s)}
                </td>
              ))}
              <td className="px-2 pt-1.5 text-right font-extrabold tabular-nums">{allTotal}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

function Stepper({
  label,
  milli,
  allowsDecimal,
  onValue,
  word = 'Opening stock',
}: {
  label: string
  milli: number | null
  allowsDecimal: boolean
  onValue: (milli: number | null) => void
  /** What the box is named by, before "of <size and colour>". */
  word?: string
}) {
  const v = milli ?? 0
  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        aria-label={`One less ${label}`}
        disabled={v <= 0}
        onClick={() => onValue(Math.max(0, v - 1000) || null)}
        className="flex size-11 items-center justify-center rounded-xl border border-border bg-surface disabled:opacity-50"
      >
        <Minus className="size-4" aria-hidden />
      </button>
      <span className="w-16">
        <QtyInput
          aria-label={`${word} of ${label}`}
          milli={milli}
          allowsDecimal={allowsDecimal}
          placeholder="0"
          className="h-11 text-center text-base font-bold"
          onValue={onValue}
        />
      </span>
      <button
        type="button"
        aria-label={`One more ${label}`}
        onClick={() => onValue(v + 1000)}
        className="flex size-11 items-center justify-center rounded-xl border border-border bg-surface"
      >
        <Plus className="size-4" aria-hidden />
      </button>
    </span>
  )
}

/** SKUs (made for you) and barcodes, with scanning tags in the grid's order. */
function Codes({
  drafts,
  labelOf,
  onEdit,
}: {
  drafts: VariantDraft[]
  labelOf: (d: VariantDraft) => string
  onEdit: (key: string, edit: VariantEdit) => void
}) {
  // Scans fill the next empty barcode; kept here so quick scans don't land on one row twice.
  const [filled, setFilled] = useState<Record<string, string>>({})
  const barcodeOf = (d: VariantDraft) => filled[d.key] ?? d.barcode
  const onCode = (code: string) => {
    const c = code.trim()
    const taken = drafts.find((d) => barcodeOf(d).trim() === c)
    if (taken) return { ok: false, message: `${c} is already ${labelOf(taken)}` }
    const next = drafts.find((d) => !barcodeOf(d).trim())
    if (!next) return { ok: false, message: 'Every size and colour has a barcode' }
    setFilled((f) => ({ ...f, [next.key]: c }))
    onEdit(next.key, { barcode: c })
    const after = drafts.find((d) => d !== next && !barcodeOf(d).trim())
    return {
      ok: true,
      message: after
        ? `${labelOf(next)}: ${c}. Next: ${labelOf(after)}`
        : `${labelOf(next)}: ${c}. All done`,
    }
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted">
        <ScanButton
          mode="continuous"
          label="Scan tags in order"
          title="Scan tags in order"
          onCode={onCode}
        />
        <span>
          Scan the tags in this order and each one fills the next empty barcode. SKUs are made for
          you.
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-background text-left text-xs text-muted">
            <tr>
              <th className="px-2 py-1.5">Size &amp; colour</th>
              <th className="px-2 py-1.5">SKU</th>
              <th className="px-2 py-1.5">Barcode</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((d) => {
              const label = labelOf(d)
              return (
                <tr key={d.key} className="border-t border-border">
                  <td className="px-2 py-1.5 font-medium whitespace-nowrap">{label}</td>
                  <td className="px-2 py-1">
                    <Input
                      aria-label={`SKU of ${label}`}
                      value={d.sku}
                      onChange={(e) => onEdit(d.key, { sku: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      aria-label={`Barcode of ${label}`}
                      value={barcodeOf(d)}
                      onChange={(e) => {
                        setFilled((f) => ({ ...f, [d.key]: e.target.value }))
                        onEdit(d.key, { barcode: e.target.value })
                      }}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The colour sheet: the shop's colours first, ready colours, patterns and Other colour. */
function ColourSheet({
  chosen,
  locked,
  shop,
  onToggle,
  onClose,
}: {
  chosen: readonly string[]
  locked: ReadonlySet<string>
  shop: ReadyColour[]
  onToggle: (colour: ReadyColour) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [other, setOther] = useState<string | null>(null)
  const isOn = (name: string) => chosen.some((c) => c.toLowerCase() === name.toLowerCase())
  const find = search.trim().toLowerCase()
  const match = (c: ReadyColour) => !find || c.name.toLowerCase().includes(find)
  const mine = new Set(shop.map((c) => c.name.toLowerCase()))
  const own = shop.filter(match)
  const ready = READY_COLOURS.filter((c) => !mine.has(c.name.toLowerCase()) && match(c))
  const patterns = PATTERNS.filter((c) => !mine.has(c.name.toLowerCase()) && match(c))
  const known = [...shop, ...READY_COLOURS, ...PATTERNS].some((c) => c.name.toLowerCase() === find)

  if (other !== null)
    return (
      <OtherColour
        name={other}
        onBack={() => setOther(null)}
        onAdd={(c) => {
          if (!isOn(c.name)) onToggle(c)
          onClose()
        }}
        onClose={onClose}
      />
    )

  const pick = (c: ReadyColour) => (
    <button
      key={c.name}
      type="button"
      aria-pressed={isOn(c.name)}
      disabled={isOn(c.name) && locked.has(c.name.toLowerCase())}
      onClick={() => onToggle(c)}
      className={cx(
        'flex h-10 min-w-0 items-center gap-2 rounded-xl border px-2.5 text-left text-sm font-semibold disabled:cursor-not-allowed',
        isOn(c.name)
          ? 'border-primary bg-sell-wash'
          : 'border-border bg-surface hover:border-primary/40',
      )}
    >
      <Swatch swatch={c.swatch} className="size-4" />
      <span className="min-w-0 flex-1 truncate">{c.name}</span>
      {isOn(c.name) && (
        <span className="flex size-4.5 items-center justify-center rounded-full bg-primary text-on-fill">
          <Check className="size-3" aria-hidden />
        </span>
      )}
    </button>
  )
  const section = (title: string, line: string, list: ReadyColour[]) =>
    list.length > 0 && (
      <section aria-label={title}>
        <h3 className="mt-4 mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
          {title}
          {line && (
            <span className="font-medium tracking-normal text-subtle normal-case"> · {line}</span>
          )}
        </h3>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">{list.map(pick)}</div>
      </section>
    )

  return (
    <Modal
      open
      onClose={onClose}
      title="Colours"
      subtitle={`Tap to add or take off. ${chosen.length} on this product.`}
      unsaved={false}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            icon={<Plus className="size-4" />}
            className="mr-auto"
            onClick={() => setOther(search.trim())}
          >
            Other colour
          </Button>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <Input
        aria-label="Find or type a colour"
        placeholder="Find or type a colour"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || !find) return
          e.preventDefault()
          const exact = [...shop, ...READY_COLOURS, ...PATTERNS].find(
            (c) => c.name.toLowerCase() === find,
          )
          if (exact) {
            if (!isOn(exact.name)) onToggle(exact)
          } else onToggle({ name: search.trim(), swatch: readySwatch(search) ?? '' })
          setSearch('')
        }}
      />
      {find && !known && (
        <p className="mt-2 text-xs text-muted">
          Press Enter to add “{search.trim()}” with no dot, or choose Other colour to give it one.
        </p>
      )}
      {section('Used in your shop', 'yours first', own)}
      {section('Ready colours', own.length > 0 ? 'the ones above are not repeated' : '', ready)}
      {section('Patterns', '', patterns)}
    </Modal>
  )
}

function OtherColour({
  name: start,
  onBack,
  onAdd,
  onClose,
}: {
  name: string
  onBack: () => void
  onAdd: (colour: ReadyColour) => void
  onClose: () => void
}) {
  const [name, setName] = useState(start)
  const [hex, setHex] = useState<string>(SHADES[21]!)
  const [hexText, setHexText] = useState<string>(SHADES[21]!)
  const [noDot, setNoDot] = useState(false)
  const clean = name.replaceAll(',', ' ').trim()
  const swatch = noDot ? '' : hex
  const choose = (h: string) => {
    setHex(h)
    setHexText(h)
    setNoDot(false)
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Other colour"
      subtitle="Kept for your shop after this."
      size="md"
      footer={
        <>
          <Button variant="ghost" className="mr-auto" onClick={onBack}>
            Back
          </Button>
          <Button disabled={!clean} onClick={() => onAdd({ name: clean, swatch })}>
            {clean ? `Add ${clean}` : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" hint="Shows on bills, receipts and labels. Change it any time.">
          {(id) => (
            <Input
              id={id}
              value={name}
              placeholder="e.g. Rani pink"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <section aria-label="Pick a shade">
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
            Pick a shade
          </h3>
          <div className="grid grid-cols-6 gap-2">
            {SHADES.map((s) => (
              <button
                key={s}
                type="button"
                aria-label={`Shade ${s}`}
                aria-pressed={!noDot && hex === s}
                onClick={() => choose(s)}
                className={cx(
                  'aspect-square w-full max-w-12 justify-self-center rounded-lg ring-1 ring-ink/15 ring-inset',
                  !noDot && hex === s && 'ring-2 ring-primary ring-offset-2 ring-offset-surface',
                )}
                style={{ background: s } as CSSProperties}
              />
            ))}
          </div>
        </section>
        <section aria-label="Exact colour">
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
            Exact colour
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick an exact colour"
              value={hex}
              onChange={(e) => choose(e.target.value)}
              className="size-10 cursor-pointer rounded-lg border border-border bg-surface p-0.5"
            />
            <span className="w-28">
              <Input
                aria-label="Colour code"
                value={hexText}
                onChange={(e) => {
                  setHexText(e.target.value)
                  const h = normalHex(e.target.value)
                  if (h) {
                    setHex(h)
                    setNoDot(false)
                  }
                }}
              />
            </span>
          </div>
        </section>
        <Checkbox
          label="No dot (for “Same as photo” or a mix)"
          checked={noDot}
          onChange={(e) => setNoDot(e.target.checked)}
        />
        <div className="flex items-center gap-2 rounded-xl border border-border bg-soft px-3 py-2.5 text-xs text-muted">
          On the product
          <span className="inline-flex h-8 items-center gap-1.5 rounded-full border-[1.5px] border-primary bg-sell-wash px-3 text-sm font-semibold text-ink">
            <Swatch swatch={swatch || undefined} />
            {clean || 'Your colour'}
          </span>
        </div>
      </div>
    </Modal>
  )
}

// ---- The product page ---------------------------------------------------------------------

/** A colour's photo: the first of its variants that has one. */
export function colourPhotos(axes: SizeColourAxes, variants: readonly ProductRow[]) {
  const out: Record<string, string> = {}
  for (const v of variants) {
    const { colour } = cellOf(axes, readValues(v.variant_values))
    if (colour && v.image_path && !out[colour]) out[colour] = v.image_path
  }
  return out
}

/** "Stock by colour and size" on a clothing group's page; null when it is not one. */
export function StockGridCard({
  group,
  variants,
  stock,
  unitSymbol,
  settings,
  swatches,
}: {
  group: ProductRow
  variants: ProductRow[]
  stock: ReadonlyMap<string, number>
  unitSymbol: string
  settings: Settings
  swatches: Record<string, string>
}) {
  const openItem = useOpen()
  const narrow = useNarrow()
  const options = readOptions(group.variant_options)
  const axes = sizeColourAxes(options)
  const photoPaths = axes ? colourPhotos(axes, variants) : {}
  const urls = usePhotoUrls(Object.values(photoPaths))
  if (!axes) return null
  const sizes: (string | null)[] = axes.size ? (options[axes.size] ?? []) : [null]
  const colours: (string | null)[] = axes.colour ? (options[axes.colour] ?? []) : [null]
  const byCell = new Map(
    variants.map((v) => {
      const c = cellOf(axes, readValues(v.variant_values))
      return [comboKey(cellValues(axes, c.size, c.colour)), v] as const
    }),
  )
  const at = (s: string | null, c: string | null) => byCell.get(comboKey(cellValues(axes, s, c)))
  const left = (v: ProductRow | undefined) => (v && v.track_stock ? (stock.get(v.id) ?? 0) : 0)
  const total = variants.reduce((a, v) => a + left(v), 0)
  const prices = sizes.map(
    (s) => colours.map((c) => at(s, c)).find(Boolean)?.sell_price_paisa ?? null,
  )
  const samePrice = new Set(prices.filter((p) => p !== null)).size <= 1
  const head = (c: string | null) => (
    <span className="inline-flex items-center gap-2.5 font-semibold">
      {c && photoPaths[c] && urls[photoPaths[c]] ? (
        <ProductPhoto
          url={urls[photoPaths[c]]}
          alt={`Photo of ${c}`}
          className="size-10 rounded-lg border border-border"
        />
      ) : (
        <Swatch swatch={c ? swatches[c] : undefined} />
      )}
      {c ?? 'Stock'}
    </span>
  )
  // One size and colour: its stock, tinted when low or out; a tap opens it. On a phone the
  // size is written in the cell, since there is no column heading.
  const stockCell = (s: string | null, c: string | null, compact: boolean) => {
    const v = at(s, c)
    if (!v)
      return (
        <span className="flex h-10 items-center justify-center rounded-xl border-[1.5px] border-dashed border-border text-xs text-subtle">
          {compact && s ? `${s} —` : '—'}
        </span>
      )
    const n = left(v)
    const out = v.track_stock && n <= 0
    const low = v.track_stock && !out && v.min_stock_milli > 0 && n <= v.min_stock_milli
    const label = variantLabel(readValues(v.variant_values), options) || v.name
    return (
      <button
        type="button"
        aria-label={`${label}: ${v.track_stock ? `${qty(n)} in stock` : 'not counted'}`}
        onClick={() => openItem(`/products/${v.id}`)}
        className={cx(
          'flex w-full items-center justify-center gap-1.5 rounded-xl border font-bold tabular-nums',
          compact ? 'h-11 text-sm' : 'h-10',
          out
            ? 'border-danger/30 bg-danger-light text-danger'
            : low
              ? 'border-warning/30 bg-warning-light text-warning'
              : 'border-border bg-surface hover:border-primary/40',
          !v.is_active && 'opacity-50',
        )}
      >
        {compact && s && <span className="font-semibold text-muted">{s}</span>}
        {v.track_stock ? qty(n) : '—'}
      </button>
    )
  }
  return (
    <section
      aria-label="Stock by colour and size"
      className="mt-4 rounded-card border border-border bg-surface p-5 shadow-card"
    >
      <h2 className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 font-semibold">
        Stock by colour and size
        <span className="text-sm font-normal text-muted">
          {variants.length} sizes &amp; colours ·{' '}
          {[qty(total), unitSymbol].filter(Boolean).join(' ')} in stock
        </span>
      </h2>
      <p className="mb-3 text-xs text-muted">
        What is in the shop now. Tap a cell to open that size and colour.
      </p>
      {narrow ? (
        <ul>
          {colours.map((c) => (
            <li key={c ?? 'none'} className="border-t border-border py-2.5 first:border-t-0">
              <div className="mb-2 flex items-center justify-between">
                {head(c)}
                <span className="text-sm font-bold tabular-nums">
                  {qty(sizes.reduce((a, s) => a + left(at(s, c)), 0))}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 min-[400px]:grid-cols-4">
                {sizes.map((s) => (
                  <span key={s ?? 'one'}>{stockCell(s, c, true)}</span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <Grid
          sizes={sizes}
          colours={colours}
          extraRow={
            !samePrice && (
              <tr>
                <th scope="row" className="py-1 pr-3 text-left text-xs font-semibold text-muted">
                  Price
                </th>
                {prices.map((p, i) => (
                  <td key={i} className="p-1 text-center text-xs text-subtle tabular-nums">
                    {p !== null ? money(p, settings) : '—'}
                  </td>
                ))}
                <td />
              </tr>
            )
          }
          head={head}
          cell={(s, c) => stockCell(s, c, false)}
          rowTotal={(c) => qty(sizes.reduce((a, s) => a + left(at(s, c)), 0))}
          colTotal={(s) => qty(colours.reduce((a, c) => a + left(at(s, c)), 0))}
          allTotal={qty(total)}
        />
      )}
    </section>
  )
}

// ---- The bill: a size and colour for a clothing product ----------------------------------

/** A group's variants and their stock, live. */
function useGroupStock(groupId: string) {
  return useLive(
    async (repo) => {
      const variants = (
        await repo.list('products', { where: { parent_id: groupId }, limit: 500 })
      ).rows.filter((v) => !v.deleted_at && v.is_active)
      const stock = new Map<string, number>()
      for (const v of variants) {
        if (!v.track_stock) continue
        const levels = await repo.stock.levels({ productId: v.id })
        stock.set(
          v.id,
          levels.reduce((a, l) => a + l.qty_milli, 0),
        )
      }
      return { variants, stock }
    },
    [groupId],
    ['products', 'stock_movements'],
  ).data
}

/**
 * The sheet a bill (or anything that sells) picks one size and colour from: the colour by
 * its photo or dot, then the size. A size out of stock is shown, not hidden; the bill warns
 * as it does for any product.
 */
export function VariantPick({
  group,
  onPick,
  onClose,
  settings,
}: {
  group: ProductRow
  onPick: (variant: ProductRow) => void
  onClose: () => void
  settings: Settings
}) {
  const data = useGroupStock(group.id)
  const options = readOptions(group.variant_options)
  const axes = sizeColourAxes(options) ?? { size: null, colour: null }
  const swatches = readSwatchesOf(group)
  const variants = data?.variants ?? []
  const photoPaths = colourPhotos(axes, variants)
  const urls = usePhotoUrls(Object.values(photoPaths))
  const colours = axes.colour ? (options[axes.colour] ?? []) : []
  const sizes = axes.size ? (options[axes.size] ?? []) : []
  const [chosen, setChosen] = useState<string | null>(null)
  const colour = colours.length === 0 ? null : (chosen ?? null)
  const at = (s: string | null, c: string | null) =>
    variants.find(
      (v) => comboKey(readValues(v.variant_values)) === comboKey(cellValues(axes, s, c)),
    )
  const left = (v: ProductRow | undefined) => (v ? (data?.stock.get(v.id) ?? 0) : 0)
  const colourLeft = (c: string) =>
    (sizes.length > 0 ? sizes : [null]).reduce((a, s) => {
      const v = at(s, c)
      return a + (v && v.track_stock ? Math.max(0, left(v)) : 0)
    }, 0)
  // A group with neither (other options): its variants by label.
  const plain = !axes.size && !axes.colour
  const needColour = colours.length > 0 && colour === null
  return (
    <Modal
      open
      onClose={onClose}
      title={group.name}
      subtitle={colour ? `Colour: ${colour}. Now the size.` : 'Pick the colour, then the size.'}
      unsaved={false}
      size="md"
    >
      {plain ? (
        <div className="grid grid-cols-2 gap-2">
          {variants.map((v) => (
            <SizeButton
              key={v.id}
              v={v}
              label={variantLabel(readValues(v.variant_values), options) || v.name}
              left={left(v)}
              settings={settings}
              onPick={onPick}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {colours.length > 0 && (
            <section aria-label="Colour">
              <h3 className="mb-2 font-semibold">Colour</h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {colours.map((c) => {
                  const path = photoPaths[c]
                  const url = path ? urls[path] : undefined
                  const none = colourLeft(c) <= 0 && variants.some((v) => v.track_stock)
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={colour === c}
                      onClick={() => setChosen(c)}
                      className={cx(
                        'grid justify-items-center gap-1 rounded-card border p-2 text-xs font-semibold',
                        colour === c
                          ? 'border-primary bg-sell-wash ring-2 ring-primary/30'
                          : 'border-border bg-surface hover:border-primary/40',
                        none && 'opacity-60',
                      )}
                    >
                      <span className="flex size-14 items-center justify-center overflow-hidden rounded-xl bg-background">
                        {url ? (
                          <ProductPhoto url={url} alt={`Photo of ${c}`} className="size-full" />
                        ) : (
                          <Swatch swatch={swatches[c]} className="size-7" />
                        )}
                      </span>
                      <span className="max-w-full truncate">{c}</span>
                      {none && <span className="font-medium text-danger">Out</span>}
                    </button>
                  )
                })}
              </div>
            </section>
          )}
          {!needColour && (
            <section aria-label="Size">
              <h3 className="mb-2 font-semibold">Size</h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {(sizes.length > 0 ? sizes : [null]).map((s) => {
                  const v = at(s, colour)
                  if (!v)
                    return (
                      <span
                        key={s ?? 'one'}
                        className="flex min-h-14 items-center justify-center rounded-xl border-[1.5px] border-dashed border-border text-xs text-subtle"
                      >
                        {s ?? 'One size'} · not kept
                      </span>
                    )
                  return (
                    <SizeButton
                      key={v.id}
                      v={v}
                      label={s ?? 'One size'}
                      left={left(v)}
                      settings={settings}
                      onPick={onPick}
                    />
                  )
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </Modal>
  )
}

function SizeButton({
  v,
  label,
  left,
  settings,
  onPick,
}: {
  v: ProductRow
  label: string
  left: number
  settings: Settings
  onPick: (v: ProductRow) => void
}) {
  const out = v.track_stock && left <= 0
  const low = v.track_stock && !out && v.min_stock_milli > 0 && left <= v.min_stock_milli
  return (
    <button
      type="button"
      aria-label={`${label}${out ? ', out of stock' : ''}`}
      onClick={() => onPick(v)}
      className={cx(
        'grid min-h-14 content-center justify-items-center rounded-xl border px-2 py-1.5 hover:border-primary/40',
        out
          ? 'border-danger/30 bg-danger-light'
          : low
            ? 'border-warning/30 bg-warning-light'
            : 'border-border bg-surface',
      )}
    >
      <span className="text-base font-bold">{label}</span>
      <span
        className={cx(
          'text-xs tabular-nums',
          out ? 'text-danger' : low ? 'text-warning' : 'text-muted',
        )}
      >
        {!v.track_stock ? money(v.sell_price_paisa, settings) : out ? 'Out' : `${qty(left)} left`}
      </span>
    </button>
  )
}

function readSwatchesOf(group: ProductRow): Record<string, string> {
  return readSwatches(group.variant_swatches)
}

// ---- Receive stock: one grid for a clothing product ----------------------------------------

/**
 * How many of each size and colour came in, as one grid (Receive stock). Adds a line for
 * every cell with a quantity; the lines are then like any other (rate, batch, free goods).
 */
export function ReceiveGrid({
  group,
  onAdd,
  onClose,
}: {
  group: ProductRow
  onAdd: (lines: { product: ProductRow; qtyMilli: number }[]) => void
  onClose: () => void
}) {
  const data = useGroupStock(group.id)
  const narrow = useNarrow()
  const options = readOptions(group.variant_options)
  const axes = sizeColourAxes(options) ?? { size: null, colour: null }
  const swatches = readSwatches(group.variant_swatches)
  const variants = data?.variants ?? []
  const sizes: (string | null)[] = axes.size ? (options[axes.size] ?? []) : [null]
  const colours: (string | null)[] = axes.colour ? (options[axes.colour] ?? []) : [null]
  const [qtys, setQtys] = useState<Record<string, number | null>>({})
  const at = (s: string | null, c: string | null) =>
    variants.find(
      (v) => comboKey(readValues(v.variant_values)) === comboKey(cellValues(axes, s, c)),
    )
  const lines = variants.flatMap((v) => {
    const q = qtys[v.id]
    return q && q > 0 ? [{ product: v, qtyMilli: q }] : []
  })
  const sum = (list: (ProductRow | undefined)[]) =>
    list.reduce((a, v) => a + (v ? (qtys[v.id] ?? 0) : 0), 0)
  const label = (v: ProductRow) => variantLabel(readValues(v.variant_values), options) || v.name
  const [phoneColour, setPhoneColour] = useState<string | null>(null)
  const shown = phoneColour ?? colours[0] ?? null
  return (
    <Modal
      open
      onClose={onClose}
      title={`Receive ${group.name}`}
      subtitle="How many of each came in. Cells left empty are not received."
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={lines.length === 0} onClick={() => onAdd(lines)}>
            {lines.length > 0
              ? `Add ${lines.length} ${lines.length === 1 ? 'line' : 'lines'} · ${qty(sum(variants))}`
              : 'Add lines'}
          </Button>
        </>
      }
    >
      {narrow ? (
        <div>
          <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1">
            {colours.map((c) => (
              <button
                key={c ?? 'none'}
                type="button"
                aria-pressed={c === shown}
                onClick={() => setPhoneColour(c)}
                className={cx(
                  'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold',
                  c === shown ? 'border-primary bg-sell-wash' : 'border-border bg-surface',
                )}
              >
                <Swatch swatch={c ? swatches[c] : undefined} />
                {c ?? 'All'}
                <span className="font-medium text-muted tabular-nums">
                  {qty(sum(sizes.map((s) => at(s, c))))}
                </span>
              </button>
            ))}
          </div>
          <ul>
            {sizes.map((s) => {
              const v = at(s, shown)
              if (!v) return null
              return (
                <li key={v.id} className="flex items-center gap-2 border-t border-border py-2">
                  <span className="w-14 font-semibold">{s ?? 'One size'}</span>
                  <span className="flex-1" />
                  <Stepper
                    label={label(v)}
                    milli={qtys[v.id] ?? null}
                    allowsDecimal={false}
                    onValue={(m) => setQtys((q) => ({ ...q, [v.id]: m }))}
                    word="Received"
                  />
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <Grid
          sizes={sizes}
          colours={colours}
          head={(c) => (
            <span className="inline-flex items-center gap-2 font-semibold">
              <Swatch swatch={c ? swatches[c] : undefined} />
              {c ?? 'Received'}
            </span>
          )}
          cell={(s, c) => {
            const v = at(s, c)
            if (!v)
              return (
                <span className="flex h-10 items-center justify-center rounded-xl border-[1.5px] border-dashed border-border text-xs text-subtle">
                  —
                </span>
              )
            return (
              <QtyInput
                aria-label={`Received of ${label(v)}`}
                milli={qtys[v.id] ?? null}
                allowsDecimal={false}
                placeholder="0"
                className="text-center"
                onValue={(m) => setQtys((q) => ({ ...q, [v.id]: m }))}
              />
            )
          }}
          rowTotal={(c) => qty(sum(sizes.map((s) => at(s, c))))}
          colTotal={(s) => qty(sum(colours.map((c) => at(s, c))))}
          allTotal={qty(sum(variants))}
        />
      )}
    </Modal>
  )
}
